import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { I18nProvider } from '@lingui/react';
import { usePathname } from 'expo-router';
import { Platform, StyleSheet, View, type ViewStyle } from 'react-native';
import { useSession } from '../SessionContext';
import { getDetectedClientPreferences } from '../clientSessionMetadata';
import { canAccessMainApp } from '../../utils/authHelpers';
import { activateLocale, createAppI18n } from './catalogs';
import {
    canActivatePersistedProductLanguage,
    getBuildLocaleOverride,
    getAutoPersistableDetectedLanguageTag,
    resolveAppLocale,
    resolveReadOnlyCachedProductLanguageTag,
    type ResolvedAppLocale,
} from './locales';
import {
    isPublicUrlLocaleAuthoritative,
    isStaticPublicWebExport,
    stripStaticPublicExportPrefix,
} from './publicWeb';
import { shouldAttemptDetectedLanguagePersistence } from './autoPersistLanguage';

type AppLocaleContextValue = ResolvedAppLocale & {
    sessionLanguageTag: string | null;
    setSessionLanguageTag: (languageTag: string | null) => boolean;
};

const AppLocaleContext = createContext<AppLocaleContextValue | null>(null);

const localeResolutionCoverStyle: ViewStyle = {
    ...StyleSheet.absoluteFill,
    backgroundColor: '#ffffff',
    zIndex: 2147483647,
    ...(Platform.OS === 'web' ? { position: 'fixed' as ViewStyle['position'] } : {}),
};

export function AppI18nProvider({ children }: { children: React.ReactNode }) {
    const {
        session,
        profile,
        isLoading: isSessionLoading,
        isProfileResolved,
        profileLoadFailed,
        refreshProfile,
        setPreferredLanguageTag,
    } = useSession();
    const pathname = usePathname();
    const logicalPathname = stripStaticPublicExportPrefix(pathname);
    const publicUrlAuthoritative = isPublicUrlLocaleAuthoritative(logicalPathname);
    const signedIn = canAccessMainApp(session);
    const persistedProductLanguageEnabled = canActivatePersistedProductLanguage();
    const staticResolution = useMemo(
        () => resolveAppLocale({ buildLocale: getBuildLocaleOverride() }),
        []
    );
    const i18n = useMemo(
        () => createAppI18n(staticResolution.messageLocale, staticResolution.formatLocale),
        [staticResolution]
    );
    const [resolvedLocale, setResolvedLocale] = useState<ResolvedAppLocale>(staticResolution);
    const [sessionLanguageTag, setSessionLanguageTagState] = useState<string | null>(null);
    const [autoPersistPendingKey, setAutoPersistPendingKey] = useState<string | null>(null);
    const [autoPersistRetryVersion, setAutoPersistRetryVersion] = useState(0);
    const attemptedDetectionRef = useRef<string | null>(null);
    const autoPersistAttemptsRef = useRef(new Map<string, number>());
    const detectedPreferences = useMemo(() => getDetectedClientPreferences(), []);
    const detectedLanguageTag = getAutoPersistableDetectedLanguageTag(
        detectedPreferences.localeTags
    );
    const cachedAuthLanguageTag = persistedProductLanguageEnabled && profileLoadFailed
        ? resolveReadOnlyCachedProductLanguageTag({
            cachedPreferredLanguageTag: session?.user.user_metadata?.preferred_language_tag,
            cachedMessageLocale: session?.user.user_metadata?.paperzilla_auth_message_locale,
            cachedProductLanguageAuthority:
                session?.user.user_metadata?.paperzilla_product_language_authority,
        })
        : null;
    const autoPersistEligible = shouldAttemptDetectedLanguagePersistence({
        publicUrlAuthoritative,
        sessionLoading: isSessionLoading,
        signedIn,
        profileResolved: isProfileResolved,
        profileUserId: profile?.id ?? null,
        sessionUserId: session?.user?.id ?? null,
        preferredLanguageTag: profile?.preferred_language_tag ?? null,
        sessionLanguageTag,
        detectedLanguageTag,
    });
    const autoPersistKey = autoPersistEligible && profile && detectedLanguageTag
        ? `${profile.id}:${detectedLanguageTag}`
        : null;
    const autoPersistAttempts = autoPersistKey
        ? autoPersistAttemptsRef.current.get(autoPersistKey) ?? 0
        : 0;
    const targetLocale = useMemo(() => {
        const buildLocale = getBuildLocaleOverride();
        return publicUrlAuthoritative
            ? resolveAppLocale({ buildLocale })
            : resolveAppLocale({
                buildLocale: isStaticPublicWebExport() ? null : buildLocale,
                profileLanguageTag: sessionLanguageTag ?? (
                    persistedProductLanguageEnabled
                        ? profile?.preferred_language_tag ?? cachedAuthLanguageTag
                        : null
                ),
                // Authenticated device detection activates only after CAS.
                // Profile-read failures may use the read-only Auth cache above,
                // but never turn device detection into a profile write.
                detectedLocaleTags: persistedProductLanguageEnabled
                    && !signedIn
                    ? detectedPreferences.localeTags
                    : [],
            });
    }, [
        cachedAuthLanguageTag,
        detectedPreferences.localeTags,
        profile,
        persistedProductLanguageEnabled,
        publicUrlAuthoritative,
        sessionLanguageTag,
        signedIn,
    ]);
    const localeActivationPending = (
        resolvedLocale.messageLocale !== targetLocale.messageLocale
        || resolvedLocale.formatLocale !== targetLocale.formatLocale
        || resolvedLocale.direction !== targetLocale.direction
    );
    const shouldDeferRender = localeActivationPending || (
        !publicUrlAuthoritative && (
            isSessionLoading
            || (signedIn && !isProfileResolved)
            || Boolean(autoPersistKey && autoPersistAttempts < 3)
            || Boolean(autoPersistPendingKey)
        )
    );

    const setSessionLanguageTag = useCallback((languageTag: string | null): boolean => {
        if (languageTag === null) {
            setSessionLanguageTagState(null);
            return true;
        }
        const resolution = resolveAppLocale({ profileLanguageTag: languageTag });
        if (resolution.fallbackUsed || !['en', 'es'].includes(resolution.messageLocale)) {
            return false;
        }
        setSessionLanguageTagState(resolution.messageLocale);
        return true;
    }, []);

    useEffect(() => {
        setSessionLanguageTagState(null);
    }, [session?.user?.id]);

    useEffect(() => {
        if (!autoPersistKey || !detectedLanguageTag) {
            return;
        }
        const attempts = autoPersistAttemptsRef.current.get(autoPersistKey) ?? 0;
        if (attempts >= 3 || attemptedDetectionRef.current === autoPersistKey) {
            return;
        }
        attemptedDetectionRef.current = autoPersistKey;
        autoPersistAttemptsRef.current.set(autoPersistKey, attempts + 1);
        setAutoPersistPendingKey(autoPersistKey);
        let cancelled = false;
        let retryTimer: ReturnType<typeof setTimeout> | null = null;
        void setPreferredLanguageTag(detectedLanguageTag).then((result) => {
            if (cancelled) return;
            if (!result.ok && result.reason === 'conflict') {
                void refreshProfile();
            }
            if (!result.ok) {
                attemptedDetectionRef.current = null;
                if (attempts + 1 < 3) {
                    retryTimer = setTimeout(() => {
                        setAutoPersistRetryVersion((value) => value + 1);
                    }, 250 * (attempts + 1));
                }
            }
        }).finally(() => {
            setAutoPersistPendingKey((currentKey) => (
                currentKey === autoPersistKey ? null : currentKey
            ));
        });

        return () => {
            cancelled = true;
            if (retryTimer) clearTimeout(retryTimer);
        };
    }, [
        autoPersistKey,
        autoPersistRetryVersion,
        detectedLanguageTag,
        refreshProfile,
        setPreferredLanguageTag,
    ]);

    useEffect(() => {
        activateLocale(i18n, targetLocale.messageLocale, targetLocale.formatLocale);
        setResolvedLocale(targetLocale);

        if (typeof document !== 'undefined') {
            document.documentElement.lang = targetLocale.messageLocale;
            document.documentElement.dir = targetLocale.direction;
        }
    }, [i18n, targetLocale]);

    const contextValue = useMemo(() => ({
        ...resolvedLocale,
        sessionLanguageTag,
        setSessionLanguageTag,
    }), [resolvedLocale, sessionLanguageTag, setSessionLanguageTag]);

    return (
        <AppLocaleContext.Provider value={contextValue}>
            <I18nProvider i18n={i18n}>
                {children}
                {shouldDeferRender ? (
                    <View
                        accessibilityElementsHidden
                        importantForAccessibility="no-hide-descendants"
                        pointerEvents="auto"
                        style={localeResolutionCoverStyle}
                        testID="locale-resolution-cover"
                    />
                ) : null}
            </I18nProvider>
        </AppLocaleContext.Provider>
    );
}

export function useAppLocale(): AppLocaleContextValue {
    const context = useContext(AppLocaleContext);
    if (!context) {
        throw new Error('useAppLocale must be used within AppI18nProvider.');
    }
    return context;
}
